// Shared content-metric aggregation.
//
// The CAMPAIGN dashboard (src/pages/CampaignDetail.js — "Organic Creator
// Performance" and "Sponsored Creator ROI") is the SOURCE OF TRUTH. This helper
// replicates its exact roll-up formulas so the client report and the exported
// PDF show the SAME numbers as the campaign, and can't drift apart again.
//
// Input: an array of raw content items using campaign field names. Only content
// for the relevant subset (organic OR sponsored) should be passed in — the caller
// splits by the per-content Paid flag first, exactly like the campaign does.
//
// Expected fields per item (missing ones default to 0):
//   platform, contentType,
//   views, impressions, likes, comments, shares, vodViews,
//   avgCcv (or streamAvgCcv), peakCcv (or streamPeakCcv), watchHours (or streamWatchHours)

// Stream classification — identical to CampaignDetail.js.
export const isStreamContent = (c) =>
  c.platform === 'twitch' ||
  c.platform === 'kick' ||
  (c.platform === 'youtube' && (c.contentType === 'stream' || c.contentType === 'vod'));

const ccvOf = (c) => c.streamAvgCcv || c.avgCcv || 0;
const peakOf = (c) => c.peakCcv || c.streamPeakCcv || 0;
const watchOf = (c) => c.watchHours || c.streamWatchHours || 0;

// Total Combined Views counts each piece by its real reach: a STREAM (Twitch or
// YouTube live) counts by its VOD views; a video/social piece counts by its views.
// This way the platform pills (non-stream views per platform) plus VOD Views
// (stream vodViews) sum exactly to the total — no hidden or double-counted views.
export const reachOf = (c) => (isStreamContent(c) ? (c.vodViews || 0) : (c.views || 0));

// --- Impressions -----------------------------------------------------------
//
// On 2026-08-24 YouTube redefined a "view" to count from the FIRST FRAME across
// every format — autoplay in the Home feed and hover-to-play now count, where
// previously a view meant someone had actually chosen to watch. The old, stricter
// definition was renamed "engaged view" and is not exposed to third parties.
//
// Our Impressions figure was always an estimate: no platform reports real
// impressions, so we assumed roughly ten thumbnail impressions per view. For
// YouTube that assumption no longer holds — a first-frame autoplay view IS
// approximately the impression, so multiplying it by ten counts the same event
// twice. YouTube's own numbers put CTR at 4-6%, which supported ~10x when a view
// meant engagement; it does not now.
//
// The multiplier therefore drops to 5x for YouTube content posted ON OR AFTER
// the change. Anything posted before it keeps 10x, deliberately: those figures
// have already gone out in client reports, and reissuing an old report must not
// silently produce different numbers. This also means NO stored data has to be
// rewritten.
//
// NOTE: backend/utils/impressions.js is the server-side twin of this logic and
// must be kept in step with it.
// Short-form is its own case entirely. On TikTok, Instagram Reels and YouTube
// Shorts a "view" means the item scrolled into the feed and autoplayed — the
// item being SHOWN is the view. There is no separate thumbnail impression to
// estimate, so the multiplier is 1x: impressions and views are the same event.
// (YouTube tracks Shorts as "shown in feed" rather than thumbnail impressions,
// which is the same thing under a different name.)
export const YT_VIEW_CHANGE_DATE = new Date('2026-08-24T00:00:00Z');

const postedOnOrAfterChange = (c) => {
  const posted = c && c.postDate ? new Date(c.postDate) : null;
  return !!posted && !isNaN(posted) && posted >= YT_VIEW_CHANGE_DATE;
};

// True for YouTube content published under the new view definition.
export const isPostViewChangeYouTube = (c) =>
  !!c && (c.platform || '').toLowerCase() === 'youtube' && postedOnOrAfterChange(c);

// Feed-autoplay formats: the view IS the impression.
// NOTE: this depends on YouTube Shorts being correctly typed as 'short'. Most
// are not — ingestion builds watch?v= URLs and the classifier only looks for
// '/shorts/' in the URL, so roughly a third of rows typed 'video' are really
// Shorts and will be estimated at 5x until that classification is fixed.
export const isShortFormContent = (c) => {
  if (!c) return false;
  const platform = (c.platform || '').toLowerCase();
  if (platform === 'tiktok' || platform === 'instagram') return true;
  return platform === 'youtube' && (c.contentType || '').toLowerCase() === 'short';
};

/**
 * Impressions for a single content item.
 *
 * @param {object} content the content item (needs platform + postDate)
 * @param {number} reach   the view figure this item is counted by
 * @param {number} [stored] an explicitly recorded impressions value, where the
 *   calling surface already preferred one. Passing it keeps that surface's
 *   existing behaviour byte-for-byte for pre-change content; omit it on
 *   surfaces that always computed from views.
 */
export function impressionsFor(content, reach, stored) {
  const r = reach || 0;
  if (postedOnOrAfterChange(content) && isShortFormContent(content)) return Math.round(r * 1);
  if (isPostViewChangeYouTube(content)) return Math.round(r * 5);
  if (stored) return stored;
  return Math.round(r * 10);
}

// Sum impressions across a list. Must be summed PER ITEM — the multiplier now
// varies by item, so multiplying an already-summed view total is wrong.
export const sumImpressions = (list, { useStored = false } = {}) =>
  (list || []).filter(Boolean).reduce(
    (s, c) => s + impressionsFor(c, reachOf(c), useStored ? c.impressions : undefined), 0);

// Aggregate a content subset the way the campaign dashboard does.
// Returns the headline figures the campaign displays as pills:
//   views        -> "Total Combined Views" (Σ reach: streams=vodViews, else views)
//   impressions  -> campaign "Impressions"      (Σ real impressions, no estimate)
//   engagement   -> campaign "Engagement"       (Σ likes+comments+shares)
//   watchHours   -> campaign "Watch Hours"      (Σ watch hours over ALL content)
//   avgCcv       -> campaign "CCV" / "Combined CCV"      (Σ over streams)
//   peakCcv      -> campaign "Peak CCV" / "Combined Peak CCV" (Σ over streams)
//   vodViews     -> campaign "VOD Views"        (Σ vodViews over streams)
//   platViews    -> per-platform NON-STREAM views (non-Twitch), e.g. { youtube, tiktok, ... }
export function aggregateContentMetrics(contents) {
  const list = (contents || []).filter(Boolean);
  const streams = list.filter(isStreamContent);
  const platViews = {};
  list.forEach((c) => {
    if (isStreamContent(c)) return; // stream reach is reported under VOD Views
    const p = (c.platform || '').toLowerCase();
    if (p && p !== 'twitch' && p !== 'kick') platViews[p] = (platViews[p] || 0) + (c.views || 0);
  });
  const views = list.reduce((s, c) => s + reachOf(c), 0);
  return {
    count: list.length,
    views,
    // No platform gives us a true impressions feed, so this stays an estimate —
    // but it is summed PER ITEM, because YouTube content posted on or after
    // 2026-08-24 uses 5x rather than 10x. See impressionsFor above.
    impressions: sumImpressions(list),
    engagement: list.reduce((s, c) => s + (c.likes || 0) + (c.comments || 0) + (c.shares || 0), 0),
    watchHours: list.reduce((s, c) => s + watchOf(c), 0),
    avgCcv: Math.round(streams.reduce((s, c) => s + ccvOf(c), 0)),
    peakCcv: Math.round(streams.reduce((s, c) => s + peakOf(c), 0)),
    vodViews: streams.reduce((s, c) => s + (c.vodViews || 0), 0),
    platViews,
  };
}
